import { useState } from 'react';
import { formatChartDate, nearestSeriesIndex } from './TimeSeriesChart';

export function sparklineCoords(values, width, height) {
  const max = Math.max(...values, 1);
  const pad = 1;
  const plotHeight = Math.max(1, height - pad * 2);
  return values.map((value, index) => ({
    x: values.length === 1 ? width / 2 : (index / (values.length - 1)) * width,
    y: pad + plotHeight - (value / max) * plotHeight,
    value,
  }));
}

export function sparklinePoints(values, width, height) {
  return sparklineCoords(values, width, height)
    .map((point) => `${point.x},${point.y}`)
    .join(' ');
}

function viewBoxSize(viewBox) {
  const parts = String(viewBox).trim().split(/[\s,]+/).map(Number);
  return {
    width: parts[2] > 0 ? parts[2] : 100,
    height: parts[3] > 0 ? parts[3] : 24,
  };
}

function pointDate(point, dateField) {
  if (!point) return '';
  return point[dateField] ?? point.date ?? point.asOf ?? '';
}

export default function Sparkline({
  series,
  field,
  dateField = 'date',
  viewBox = '0 0 100 24',
  className = 'sparkline',
  emptyMessage,
  area = false,
  formatValue,
}) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const points = series || [];
  const values = points.map((point) => point[field] ?? 0);
  if (values.length < 2) {
    if (emptyMessage) {
      return <p className="chart-empty">{emptyMessage}</p>;
    }
    return <div className="sparkline sparkline-empty" aria-hidden="true" />;
  }

  const { width, height } = viewBoxSize(viewBox);
  const coords = sparklineCoords(values, width, height);
  const linePoints = coords.map((point) => `${point.x},${point.y}`).join(' ');
  const areaPoints = `${linePoints} ${width},${height} 0,${height}`;
  const formatTick = formatValue || ((value) => String(value));
  const peakIndex = values.reduce((best, value, index) => (value > values[best] ? index : best), 0);
  const peakDate = formatChartDate(pointDate(points[peakIndex], dateField));
  const label = `${field} over ${points.length} points. Peak ${formatTick(values[peakIndex])}${
    peakDate ? ` on ${peakDate}` : ''
  }.`;

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) return;
    const x = ((event.clientX - rect.left) / rect.width) * width;
    setHoverIndex(nearestSeriesIndex(x, coords.map((point) => point.x)));
  };

  const hover = hoverIndex != null ? coords[hoverIndex] : null;
  const hoverPoint = hoverIndex != null ? points[hoverIndex] : null;

  return (
    <div className="chart-hover sparkline-hover" onMouseLeave={() => setHoverIndex(null)}>
      <svg
        className={className}
        viewBox={viewBox}
        preserveAspectRatio="none"
        role="img"
        aria-label={label}
        onMouseMove={handleMove}
      >
        {area ? <polygon className="sparkline-area" points={areaPoints} /> : null}
        <polyline className="sparkline-line" points={linePoints} />
        {hover ? (
          <circle className="sparkline-dot" cx={hover.x} cy={hover.y} r="1.6" />
        ) : null}
      </svg>
      {hover && hoverPoint ? (
        <div
          className="chart-hover-tip"
          style={{ left: `${(hover.x / width) * 100}%` }}
        >
          {formatChartDate(pointDate(hoverPoint, dateField))}: {formatTick(hover.value)}
        </div>
      ) : null}
    </div>
  );
}
